import { merchantName } from '../features/agency/merchantName';
import { get } from '@aiptyg/api-client';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import * as api from '../features/agency/service';
import {
  Badge,
  button,
  primary,
  input,
  Panel,
  Notice,
  Pager,
  useCan,
  useFilters,
  useResource,
  errorText,
} from '../features/agency/ui';
import { formatDateTime } from '../lib/format';
const empty = {
  name: '',
  contact_name: '',
  contact_phone: '',
  notes: '',
  allowed_task_types: ['normal', 'other_normal', 'message_send'],
  advertising_enabled: false,
  promotion_require_review: true,
  market_id: 'CN',
  username: '',
  password: '',
  display_name: '',
};
const taskTypeLabels: Record<string, string> = { normal: '普通任务', download: '下载任务', other_normal: '其他普通任务', message_send: '短信代发' };
const promotionTaskTypes = ['normal', 'download', 'other_normal'];
const promotionPermissionLabel = (types: string[] = []) => types.filter((type) => promotionTaskTypes.includes(type)).map((type) => taskTypeLabels[type]).join('、') || '未开通';
const passwordError = (value: string) => {
  const bytes = new TextEncoder().encode(value).length;
  return bytes < 12 || bytes > 72 ? `密码需为 12–72 字节，当前 ${bytes} 字节（中文通常占 3 字节）` : '';
};
export function AgentsPage() {
  const { q, set, page } = useFilters();
  const canManage = useCan('agents.manage');
  const r = useResource(
    () =>
      api.listAgents({
        page,
        page_size: 20,
        search: q.get('search') ?? '',
        status: q.get('status') ?? '',
      }),
    [q.toString()],
  );
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState<api.Agent>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const validation = {
    name: form.name.trim() ? '' : '请填写商户名称',
    username: edit ? '' : !form.username.trim() ? '请填写独立登录账号' : /^[a-z0-9._@-]{3,100}$/i.test(form.username.trim()) ? '' : '账号需为 3–100 位英文字母、数字或 . _ - @',
    password: edit ? '' : passwordError(form.password),
    permissions: form.allowed_task_types.length || form.advertising_enabled ? '' : '请至少开通一种任务权限或广告权限',
  };
  const save = async () => {
    if (busy || Object.values(validation).some(Boolean)) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await api.saveAgent(edit?.id, {
        ...form,
        expected_version: edit?.version,
      });
      setSuccess(edit ? '商户资料与权限已保存' : '商户已创建，首次登录需修改初始密码');
      setForm(empty);
      setEdit(undefined);
      r.reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        总后台统一管理中国商户，商户通过独立后台登录和发布任务。
      </p>
      <Notice error={r.error || error} />
      {success && <p role="status" className="text-sm text-green-700">{success}</p>}
      <Panel>
        <div className="flex flex-wrap gap-3">
          <input
            aria-label="搜索商户"
            className={input}
            placeholder="搜索商户"
            value={q.get('search') ?? ''}
            onChange={(e) => set('search', e.target.value)}
          />
          <select
            aria-label="商户状态"
            className={input}
            value={q.get('status') ?? ''}
            onChange={(e) => set('status', e.target.value)}
          >
            <option value="">全部状态</option>
            <option value="active">正常</option>
            <option value="login_disabled">禁用登录</option>
            <option value="publish_restricted">限制发布</option>
            <option value="business_frozen">冻结业务</option>
          </select>
          <button className={button} onClick={r.reload}>
            刷新
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm text-left">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                {[
                  '商户',
                  '联系人',
                  '业务权限',
                  '登录 / 发布 / 业务',
                  '创建时间',
                  '操作',
                ].map((t) => (
                  <th className="p-3" key={t}>
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.data?.items.map((a) => (
                <tr key={a.id} className="border-b border-gray-100">
                  <td className="p-3">
                    <Link className="text-primary" to={`/agents/${a.id}`}>
                      {a.name}
                    </Link>
                    <div className="text-xs text-gray-400">
                      #{a.id} · 中国 CNY
                    </div>
                  </td>
                  <td className="p-3">
                    {a.contact_name || '-'}
                    <div className="text-xs text-gray-500">
                      {a.contact_phone}
                    </div>
                  </td>
                  <td className="p-3">
                    <div>App推广：{promotionPermissionLabel(a.allowed_task_types)}</div><div className="text-xs text-gray-500">会员提交：{(a.promotion_require_review ?? true) ? "人工审核" : "系统校验后结算"}</div>
                    <div className="mt-1 text-xs text-gray-500">短信代发：{a.allowed_task_types?.includes('message_send') ? '已开通' : '未开通'}</div>
                    <div className="mt-1 text-xs text-gray-500">广告权限：{a.advertising_enabled === true ? '已开通' : '未开通'}</div>
                  </td>
                  <td className="p-3">
                    {a.login_disabled ? '登录禁用' : '可登录'} /{' '}
                    {a.publish_restricted ? '发布受限' : '可发布'} /{' '}
                    {a.business_frozen ? '业务冻结' : '正常'}
                  </td>
                  <td className="p-3">{formatDateTime(a.created_at)}</td>
                  <td className="p-3">
                    {canManage && (
                      <div className="flex flex-wrap gap-3">
                      <button
                        className="text-primary"
                        onClick={() => {
                          setError('');
                          setSuccess('');
                          setEdit(a);
                          setForm({
                            name: a.name,
                            contact_name: a.contact_name,
                            contact_phone: a.contact_phone,
                            notes: a.notes,
                            allowed_task_types: a.allowed_task_types ?? [],
                            advertising_enabled: a.advertising_enabled === true,
                            promotion_require_review: a.promotion_require_review ?? true,
                            market_id: 'CN',
                            username: '',
                            password: '',
                            display_name: '',
                          });
                        }}
                      >
                        编辑
                      </button>
                      <Link className="text-primary whitespace-nowrap" to={`/agents/${a.id}#accounts`}>重置密码</Link>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.loading ? (
            <p className="py-8 text-center text-gray-400">加载中…</p>
          ) : (
            !r.data?.items.length && (
              <p className="py-8 text-center text-gray-400">暂无商户</p>
            )
          )}
        </div>
        <Pager
          total={r.data?.total ?? 0}
          page={page}
          change={(v) => set('page', String(v))}
        />
      </Panel>
      {canManage && (
        <Panel title={edit ? `编辑商户 #${edit.id}` : '创建商户'}>
          <div className="grid gap-3 md:grid-cols-3">
            {(['name', 'contact_name', 'contact_phone'] as const).map(
              (key, i) => (
                <label key={key} className="text-sm text-gray-600">
                  {['商户名称', '联系人', '联系电话'][i]}
                  <input
                    aria-label={['商户名称', '联系人', '联系电话'][i]}
                    aria-invalid={key === 'name' && !!validation.name}
                    aria-describedby={key === 'name' ? 'agent-name-error' : undefined}
                    className={`${input} w-full mt-1`}
                    maxLength={key === 'name' ? 120 : key === 'contact_phone' ? 40 : 100}
                    value={form[key]}
                    onChange={(e) =>
                      setForm({ ...form, [key]: e.target.value })
                    }
                  />
                  {key === 'name' && validation.name && <small id="agent-name-error" className="mt-1 block text-red-600">{validation.name}</small>}
                </label>
              ),
            )}
          </div>
          {!edit && (
            <div className="grid gap-3 md:grid-cols-3">
              {(['username', 'display_name', 'password'] as const).map(
                (key, i) => (
                  <label key={key} className="text-sm text-gray-600">
                    {['独立登录账号', '显示名称', '初始密码（12–72字节）'][i]}
                    <input
                      aria-label={['独立登录账号', '显示名称', '初始密码'][i]}
                      aria-invalid={key !== 'display_name' && !!validation[key]}
                      aria-describedby={key !== 'display_name' ? `agent-${key}-error` : undefined}
                      autoComplete={key === 'password' ? 'new-password' : key === 'username' ? 'username' : undefined}
                      maxLength={key === 'username' ? 100 : undefined}
                      type={key === 'password' ? 'password' : 'text'}
                      className={`${input} w-full mt-1`}
                      value={form[key]}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.value })
                      }
                    />
                    {key !== 'display_name' && validation[key] && <small id={`agent-${key}-error`} className="mt-1 block text-red-600">{validation[key]}</small>}
                  </label>
                ),
              )}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-3" role="group" aria-label="业务权限">
            <fieldset className="rounded-md border border-gray-200 p-3 text-sm">
              <legend className="px-1 font-medium"><label><input type="checkbox" checked={promotionTaskTypes.some((type) => form.allowed_task_types.includes(type))} ref={(node) => { if (node) node.indeterminate = promotionTaskTypes.some((type) => form.allowed_task_types.includes(type)) && !promotionTaskTypes.every((type) => form.allowed_task_types.includes(type)); }} onChange={(e) => setForm({ ...form, allowed_task_types: e.target.checked ? [...form.allowed_task_types.filter((type) => !promotionTaskTypes.includes(type)), ...promotionTaskTypes] : form.allowed_task_types.filter((type) => !promotionTaskTypes.includes(type)) })} />{' '}App推广</label></legend>
              <div className="space-y-2">
                {([['normal', '普通任务（含身份核验）'], ['download', '下载任务'], ['other_normal', '其他普通任务']] as const).map(([key, label]) => <label key={key} className="block"><input type="checkbox" checked={form.allowed_task_types.includes(key)} onChange={(e) => setForm({ ...form, allowed_task_types: e.target.checked ? [...form.allowed_task_types, key] : form.allowed_task_types.filter((type) => type !== key) })} />{' '}{label}</label>)}
              </div>
              <label className="mt-3 block border-t pt-3"><input type="checkbox" checked={form.promotion_require_review} onChange={(e) => setForm({ ...form, promotion_require_review: e.target.checked })} /> 会员提交结果需要人工审核</label>
              <p className="mt-2 text-xs text-gray-500">仅作用于 App 推广。关闭后，系统校验通过且未触发风控时自动结算；任务发布仍需平台审核。变更影响后续提交的任务版本，已有领取保持原规则。</p>
            </fieldset>
            <fieldset className="rounded-md border border-gray-200 p-3 text-sm">
              <legend className="px-1 font-medium"><label><input type="checkbox" checked={form.allowed_task_types.includes('message_send')} onChange={(e) => setForm({ ...form, allowed_task_types: e.target.checked ? [...form.allowed_task_types, 'message_send'] : form.allowed_task_types.filter((type) => type !== 'message_send') })} />{' '}短信代发</label></legend>
              <p className="text-xs text-gray-500">独立管理短信发送任务、提交记录与报表。</p>
            </fieldset>
            <fieldset className="rounded-md border border-gray-200 p-3 text-sm">
              <legend className="px-1 font-medium"><label><input type="checkbox" checked={form.advertising_enabled} onChange={(e) => setForm({ ...form, advertising_enabled: e.target.checked })} />{' '}发布广告</label></legend>
              <p className="text-xs text-gray-500">开通后可管理广告图片、链接与排期，审核通过后在福利专区展示。</p>
            </fieldset>
          </div>
          {validation.permissions && <p role="alert" className="text-sm text-red-600">{validation.permissions}</p>}
          <p className="text-xs text-gray-500">广告权限支持管理广告、上传广告素材和查看广告报表，可单独开通。关闭后不可访问广告业务，已审核通过的广告继续按原投放规则运行。</p>
          <label className="block text-sm text-gray-600">
            内部备注
            <textarea
              className="mt-1 w-full rounded-md border border-gray-300 p-3"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          <div className="flex gap-3">
            <button
              className={primary}
              disabled={busy || Object.values(validation).some(Boolean)}
              onClick={save}
            >
              {busy ? '保存中…' : '保存'}
            </button>
            {edit && (
              <button
                className={button}
                onClick={() => {
                  setEdit(undefined);
                  setForm(empty);
                }}
              >
                取消编辑
              </button>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
export function AgentDetailPage() {
  const location = useLocation();
  const id = Number(useParams().id);
  const r = useResource(() => api.getAgent(id), [id]);
  const canManage = useCan('agents.manage');
  const canLogs = useCan('agent_logs.view');
  const canTasks = useCan('agent_tasks.view');
  const canReports = useCan('agent_reports.view');
  const acc = useResource(
    () => (canManage ? api.accounts(id) : Promise.resolve({ items: [] })),
    [id, canManage],
  );
  const logs = useResource(
    () =>
      canLogs
        ? api.listAudit({ agent_id: id, page: 1, page_size: 20 })
        : Promise.resolve({ items: [], total: 0, page: 1, page_size: 20 }),
    [id, canLogs],
  );
  const taskList = useResource(
    () =>
      canTasks
        ? api.listAgentTasks({ agent_id: id, page: 1, page_size: 5 })
        : Promise.resolve({ items: [], total: 0, page: 1, page_size: 5 }),
    [id, canTasks],
  );
  const stats = useResource(
    () =>
      canReports
        ? get<{
            overview: {
              claimed_people: number;
              claimed_shares: number;
              approved_shares: number;
              pending_review_shares: number;
            };
          }>(`/v1/admin/agent-reports?agent_id=${id}`)
        : Promise.resolve(undefined),
    [id, canReports],
  );
  useEffect(() => {
    if (location.hash === '#accounts' && r.data) document.getElementById('accounts')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash, r.data]);
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const act = async (fn: () => Promise<unknown>, feedback = '商户控制状态已保存') => {
    if (busy) return;
    setSuccess('');
    setBusy(true);
    setError('');
    try {
      await fn();
      setSuccess(feedback);
      r.reload();
      acc.reload();
      logs.reload();
      setReason('');
      setPassword('');
      setResetOpen(false);
      setFlags({});
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const a = r.data;
  return (
    <div className="space-y-5">
      <Link className="text-sm text-primary" to="/agents">
        返回商户列表
      </Link>
      <Notice error={r.error || acc.error || error} />
      {success && <p role="status" className="text-sm text-green-700">{success}</p>}
      {a && (
        <>
          <Panel title={`${a.name} #${a.id}`}>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <p>
                联系人：{a.contact_name || '-'} {a.contact_phone}
              </p>
              <p>创建：{formatDateTime(a.created_at)}</p>
              <p>中国 · CNY · 版本 {a.version}</p>
            </div>
            <p className="text-sm">App推广：{promotionPermissionLabel(a.allowed_task_types)}；短信代发：{a.allowed_task_types?.includes('message_send') ? '已开通' : '未开通'}；发布广告：{a.advertising_enabled === true ? '已开通' : '未开通'}</p>
            <p className="text-sm text-gray-500">内部备注：{a.notes || '无'}</p>
            <div className="flex gap-4 text-sm">
              {canTasks && (
                <Link
                  className="text-primary"
                  to={`/agent-tasks?agent_id=${id}`}
                >
                  查看全部商户任务
                </Link>
              )}
              {canReports && (
                <Link
                  className="text-primary"
                  to={`/agent-reports?agent_id=${id}`}
                >
                  查看在线统计
                </Link>
              )}
              {canLogs && (
                <Link
                  className="text-primary"
                  to={`/agent-audit?agent_id=${id}`}
                >
                  查看完整审计与登录日志
                </Link>
              )}
            </div>
          </Panel>
          {canReports && (
            <Panel title="本月任务统计">
              <Notice error={stats.error} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  {
                    label: '领取人数',
                    value: stats.data?.overview.claimed_people,
                  },
                  {
                    label: '领取份数',
                    value: stats.data?.overview.claimed_shares,
                  },
                  {
                    label: '通过份数',
                    value: stats.data?.overview.approved_shares,
                  },
                  {
                    label: '待审核份数',
                    value: stats.data?.overview.pending_review_shares,
                  },
                ].map((x) => (
                  <div className="rounded-md bg-gray-50 p-4" key={x.label}>
                    <p className="text-xs text-gray-500">{x.label}</p>
                    <p className="mt-2 text-2xl font-semibold">
                      {x.value ?? '—'}
                    </p>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {canTasks && (
            <Panel
              title={`最近商户任务 · 共 ${taskList.data?.total ?? '—'} 个`}
            >
              <Notice error={taskList.error} />
              {taskList.data?.items.map((t) => (
                <div
                  key={t.id}
                  className="flex justify-between gap-3 border-b border-gray-100 py-3 text-sm"
                >
                  <Link className="text-primary" to={`/agent-tasks/${t.id}`}>
                    #{t.id} {t.payload.title}
                  </Link>
                  <Badge value={t.status} />
                </div>
              ))}
              {!taskList.loading && !taskList.data?.items.length && (
                <p className="text-sm text-gray-400">暂无商户任务</p>
              )}
            </Panel>
          )}
          {canManage && (
            <Panel title="账号与业务控制">
              <div className="space-y-3">
                {(
                  [
                    [
                      'login_disabled',
                      '禁用登录',
                      '立即撤销现有会话，商户账号无法登录；已有任务和会员审核继续。',
                    ],
                    [
                      'publish_restricted',
                      '限制发布',
                      '允许登录、查看与编辑草稿，禁止提交发布审核；线上任务继续。',
                    ],
                    [
                      'business_frozen',
                      '冻结业务',
                      '停止新建、提交和新领取，已有会员提交按原快照继续审核。',
                    ],
                  ] as const
                ).map(([k, t, n]) => (
                  <label key={k} className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={flags[k] ?? a[k]}
                      onChange={(e) =>
                        setFlags({ ...flags, [k]: e.target.checked })
                      }
                    />
                    <span>
                      {t}
                      <small className="block mt-1 text-gray-500">{n}</small>
                    </span>
                  </label>
                ))}
              </div>
              <input
                aria-label="控制原因"
                className={`${input} w-full`}
                placeholder="填写状态变更原因（必填）"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                className={primary}
                disabled={busy || !reason.trim()}
                onClick={() =>
                  act(() =>
                    api.setAgentStatus(a, {
                      login_disabled: flags.login_disabled ?? a.login_disabled,
                      publish_restricted:
                        flags.publish_restricted ?? a.publish_restricted,
                      business_frozen:
                        flags.business_frozen ?? a.business_frozen,
                      reason,
                    }),
                  )
                }
              >
                保存控制状态
              </button>
            </Panel>
          )}
          {canManage && (
            <div id="accounts" className="scroll-mt-24"><Panel title="独立登录账号">
              <p className="text-sm text-gray-500">重置后该商户的全部登录账号将使用新密码，现有会话立即失效；下次登录必须修改密码。</p>
              <Notice error={acc.error} />
              {acc.data?.items.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-wrap justify-between gap-3 border-b border-gray-100 py-3 text-sm"
                >
                  <span>
                    {item.username} · {item.display_name} ·{' '}
                    {item.disabled ? '禁用' : '正常'}
                    <small className="block text-gray-500">
                      最近登录：
                      {item.last_login_at
                        ? formatDateTime(item.last_login_at)
                        : '从未登录'}
                    </small>
                  </span>

                </div>
              ))}
              {!!acc.data?.items.length && !resetOpen && <button className={button} disabled={busy} onClick={() => { setPassword(''); setError(''); setSuccess(''); setResetOpen(true); }}>重置商户全部账号密码</button>}
              {resetOpen && (
                <div className="space-y-3">
                  <p className="text-sm">确认重置商户「{a.name}」的全部登录账号密码？</p>
                  <div className="flex flex-wrap gap-3">
                  <input
                    aria-label="重置后的密码"
                    aria-invalid={!!passwordError(password)}
                    aria-describedby="reset-password-error"
                    type="password"
                    autoComplete="new-password"
                    className={input}
                    placeholder="新密码（12–72字节）"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    className={primary}
                    disabled={
                      busy ||
                      !!passwordError(password)
                    }
                    onClick={() =>
                      !passwordError(password) && act(() => api.resetPassword(id, password), '密码已重置，原会话已撤销；商户下次登录必须修改密码')
                    }
                  >
                    确认重置并撤销会话
                  </button>
                  <button
                    className={button}
                    disabled={busy}
                    onClick={() => { setResetOpen(false); setPassword(''); }}
                  >
                    取消
                  </button>
                  </div>
                  {passwordError(password) && <p id="reset-password-error" className="text-sm text-red-600">{passwordError(password)}</p>}
                </div>
              )}
            </Panel></div>
          )}
          {canLogs && (
            <Panel title="最近操作 / 登录记录">
              <AuditRows items={logs.data?.items ?? []} />
              <Notice error={logs.error} />
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
export function AuditRows({ items }: { items: api.Audit[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm text-left">
        <thead className="bg-gray-50">
          <tr>
            {['时间', '商户 / 任务', '操作人', '动作', 'IP', '变更记录'].map(
              (t) => (
                <th className="p-3" key={t}>
                  {t}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.id} className="border-b border-gray-100">
              <td className="p-3 whitespace-nowrap">
                {formatDateTime(x.created_at)}
              </td>
              <td className="p-3">
                {merchantName(x)} / 任务 #{x.task_id ?? x.portal_task_id ?? '-'}
              </td>
              <td className="p-3">
                <Badge value={x.actor_kind} /> #{x.actor_id ?? '-'}
              </td>
              <td className="p-3">
                {(
                  {
                    'agent.create': '创建商户',
                    'agent.edit': '编辑商户',
                    'agent.status': '变更业务状态',
                    'auth.login': '账号登录',
                    'auth.password_changed': '修改密码',
                    'auth.password_reset': '管理员重置密码',
                    'task.create': '新建任务',
                    'task.edit': '编辑任务版本',
                    'task.submit': '提交发布审核',
                    'task.review': '发布审核',
                    'task.active': '恢复上线',
                    'task.paused': '暂停任务',
                    'task.offline': '下架任务',
                    'evidence.redacted': '发布脱敏证据',
                    'evidence.redaction_failed': '脱敏处理失败',
                    'evidence.viewed': '查看脱敏证据',
                    'evidence.original_viewed': '查看原图',
                  } as Record<string, string>
                )[x.action] ?? x.action}
              </td>
              <td className="p-3">{x.ip || '-'}</td>
              <td className="p-3">
                <details>
                  <summary className="cursor-pointer text-primary">
                    查看
                  </summary>
                  <pre className="max-w-sm overflow-auto whitespace-pre-wrap text-xs">
                    {JSON.stringify(x.metadata, null, 2)}
                  </pre>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!items.length && (
        <p className="py-8 text-center text-gray-400">暂无记录</p>
      )}
    </div>
  );
}
export function AgentAuditPage() {
  const { q, set, page } = useFilters();
  const r = useResource(
    () =>
      api.listAudit({
        page,
        page_size: 20,
        agent_id: q.get('agent_id') ?? '',
        action: q.get('action') ?? '',
        search: q.get('search') ?? '',
      }),
    [q.toString()],
  );
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        商户创建、账号登录、密码重置、任务编辑、审核与业务控制的完整操作轨迹。
      </p>
      <Notice error={r.error} />
      <Panel>
        <div className="flex flex-wrap gap-3">
          <input
            aria-label="商户 ID"
            className={input}
            placeholder="商户 ID"
            value={q.get('agent_id') ?? ''}
            onChange={(e) => set('agent_id', e.target.value)}
          />
          <input
            aria-label="动作筛选"
            className={input}
            placeholder="动作关键词"
            value={q.get('search') ?? ''}
            onChange={(e) => set('search', e.target.value)}
          />
          <button className={button} onClick={r.reload}>
            刷新
          </button>
        </div>
        <AuditRows items={r.data?.items ?? []} />
        <Pager
          total={r.data?.total ?? 0}
          page={page}
          change={(v) => set('page', String(v))}
        />
      </Panel>
    </div>
  );
}
